import React from 'react'
import { styled } from '@mui/material/styles'
import { Logger } from 'wdc-cube'
import { FCClassContext, classToFComponent } from 'src/utils/views'

import { DataGrid, GridColDef, GridRowParams, GridRowClassNameParams } from '@mui/x-data-grid'

import TcmProgress from './helpers/tcm_progress'
import StyledDataGrid from './helpers/tcm_datagrid'
import { formatNumber } from './helpers/tcm_view_utils'
import { TableAtividadeEconomicaScope, TableAtividadeEconomicaRow } from '../tcm_scopes'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-View-AtivEconomica')

const View = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    zoom: 0.9
})

const CustomDataGrid = StyledDataGrid as typeof DataGrid

const GRID_COLUMN_DEFS = newGridColumnDefs(true)
const GRID_COLUMN_DEFS_COMPACT = newGridColumnDefs(false)

export type AtividadeEconomicaTableViewProps = {
    className?: string
    style?: React.CSSProperties
    scope: TableAtividadeEconomicaScope
    compact?: boolean
}

export class AtividadeEconomicaTableViewClass implements FCClassContext<AtividadeEconomicaTableViewProps> {
    // :: Fields
    scope!: TableAtividadeEconomicaScope
    loaded = false
    loading = true

    onSyncState(props: AtividadeEconomicaTableViewProps) {
        this.scope = props.scope
        this.loaded = this.scope.loaded
        this.loading = !this.loaded
    }

    render(props: AtividadeEconomicaTableViewProps): React.JSX.Element {
        const { loading, loaded, scope } = this
        const compact = props.compact ?? false
        return (
            <TcmCard caption={scope.caption} className={props.className} style={props.style}>
                <View style={compact ? { zoom: 1 } : undefined}>
                    {loading && <TcmProgress />}
                    {loaded && (
                        <CustomDataGrid
                            rows={[...scope.dataset]}
                            columns={compact ? GRID_COLUMN_DEFS_COMPACT : GRID_COLUMN_DEFS}
                            density={compact ? 'compact' : undefined}
                            disableRowSelectionOnClick
                            hideFooterPagination
                            hideFooter
                            getRowId={getRowId}
                            onRowClick={this.emitOnRowClick}
                            getRowClassName={this.computeRowClassName}
                            disableColumnSelector
                        />
                    )}
                </View>
            </TcmCard>
        )
    }

    emitOnRowClick = (evt: GridRowParams) => {
        this.scope.onItemClicked(evt.id as string).catch(LOG.caught)
    }

    computeRowClassName = (params: GridRowClassNameParams<TableAtividadeEconomicaRow>) => {
        return params.row.selected ? 'super-app-theme-selected' : ''
    }
}

export default classToFComponent(AtividadeEconomicaTableViewClass, React)

function getRowId(row: TableAtividadeEconomicaRow) {
    return row.id
}

function renderIdentityCell({ value }: { value?: unknown }) {
    const label = (value as string) ?? '—'
    return (
        <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
            {label}
        </span>
    )
}

function newGridColumnDefs(boldIdentity: boolean): GridColDef[] {
    return [
        {
            field: 'id',
            headerName: 'CNAE',
            width: 78,
            renderCell: boldIdentity ? renderIdentityCell : undefined
        },
        {
            field: 'descricao',
            headerName: 'Descrição CNAE',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'qtdEmpresas',
            headerName: 'Empresas',
            width: 86,
            type: 'number',
            valueFormatter: (value) => formatNumber(Number(value))
        },
        {
            field: 'qtdFuncionarios',
            headerName: 'Funcion.',
            width: 84,
            type: 'number',
            valueFormatter: (value) => formatNumber(Number(value))
        }
    ]
}
